import { DateTime } from "luxon";

import {
    CUSTOM_SUPPLIER_TYPE,
    DELIVERY_DATE_TYPE,
    INDUSTRY_TYPE,
    KIND,
    LINK_TYPE,
    ORDER_DATE,
    ORDER_DATE_PICKUP,
    PAYMENT_METHOD,
    PAYMENT_OPTIONS
} from "@/types/enum/supplierEnum";
import { PartiallyRequired } from "@/types/helper";
import { REPEAT_CYCLE, WEEKDAY } from "@/types/enum/general";
import { DELIVERY_TYPE } from "@/types/enum/order";
import { SORT_METHOD } from "@/types/enum/propEnum";
import { UrlFile } from "@/types/notification";
import { PROMOTION_TYPE, STAKEHOLDER_KIND } from "@/types/enum/notification";

// Interfaces
export interface Distribution {
    method: DELIVERY_TYPE.DELIVERY | DELIVERY_TYPE.PICKUP;
    pickupLocation?: string;
}

export interface SupplierSettings {
    distribution?: Distribution;
    paymentMethod?: PAYMENT_METHOD;
}

export interface CustomSupplier {
    name: string;
    email: string;
    customerNo: string,
    industry: INDUSTRY_TYPE;
    streetName?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    phone?: string;
    iconType?: CUSTOM_SUPPLIER_TYPE;
}

export interface Supplier extends CustomSupplier {
    id: string,
    fax?: string,
    contact?: string,
    paymentOptions?: PAYMENT_OPTIONS,
    orderHint?: string,
    imageLink?: string,
    deliveryTiming?: ORDER_DATE,
    deliveryDays?: number[],
    pickupTiming?: ORDER_DATE | ORDER_DATE_PICKUP,
    pickupLocations?: string[],
    hasCoupon?: boolean,
    allowsOrderMessage?: boolean,
    kind?: KIND;
    settings: SupplierSettings;
    hasSpecialOffers: boolean;
    scannerActivated?: boolean;
    hasCallBot?: boolean;
}

export interface DeliveryDate {
    date: string;
    type: DELIVERY_DATE_TYPE;
    selectable: boolean;
    available: boolean;
}

export interface VariantItem {
    id: string;
    name: string;
    description?: string;
    eans: number[]
}

export interface StakeholderBase {
    id: string;
    kind: STAKEHOLDER_KIND;
    name: string;
    isDeleted?: boolean;
}

export interface Manufacturer extends StakeholderBase {
    kind: STAKEHOLDER_KIND.MANUFACTURER
}

export interface Octopus extends StakeholderBase {
    kind: STAKEHOLDER_KIND.OCTOPUS;
    key: string;
}

export interface Advertiser extends StakeholderBase {
    kind: STAKEHOLDER_KIND.ADVERTISER
}

export interface Crew extends StakeholderBase {
    kind: STAKEHOLDER_KIND.CREW
}

export interface SupplierNew extends StakeholderBase { //TODO: Replace old Supplier
    kind: STAKEHOLDER_KIND.SUPPLIER;
    sId: number;
    iconType?: string;
    hasReducedCatalog?: boolean;
}

export type Stakeholder = SupplierNew | Crew | Advertiser | Manufacturer | Octopus

export interface MasterItem {
    id: string;
    name: string;
    manufacturer: Manufacturer;
    variants: VariantItem;
    image?: UrlFile;
}

export interface ProductMaster {
    id: string;
    name: string;
    type: LINK_TYPE.MASTER_CATALOG;
    item: MasterItem;
    note?: string;
    limitedSupply?: boolean;
    recommendedAmount?: number;
    limitOrderSize?: number;
    stepAmount?: number;
    variantId: string;
    availableBy?: string[]
}

export interface CatalogItem {
    id: string,
    name: string,
    itemNumber?: string,
    price?: number,
    unitPrice?: number,
    imageLink?: string,
    packaging?: string,
    amountStep?: number,
    amountMax?: number,
    itemGroup1?: string,
    itemGroup2?: string,
    availabilityInformation?: string,
    isSpecialOffer?: boolean,
    promotions?: { id: string; title: string; productId: string }[],
    promotion?: { id: string; title: string; productId: string; shortTitle?: string; promotionType?: PROMOTION_TYPE },
    notInStock?: boolean,
    supplierId?: string,
    targetStock?: number,
    tags?: PartiallyRequired<CategoryTag, 'id'>[]
    inList?: boolean;
    quantity?: number;
    listItemId?: string;
    eanCode?: string;
	masterItem?: string;
}

export interface CatalogParams {
    supplierId: string,
    locationId: string,
    take?: number,
    skip?: number,
    searchKey?: string,
    sortField?: string,
    end?: number,
    category?: string | string[],
    specialOffersOnly?: boolean
    includeList?: string;
}

export type EditList = Omit<List, 'inCart'>;

export interface List {
    id?: string,
    sId?: number;
    name: string,
    supplierId: string,
    creatorId?: string,
    description: string,
    shouldDelete?: boolean;
    positionCount?: number;
    itemCount?: number;
    compatibilityMode?: boolean;
    inCart?: boolean;
    totalPosition?: number;
}

export type CartListItem = ListItem & {
    promotion?: { id: string; validUntil?: string };
    isPromotion?: boolean;
    promotionId?: string;
    productId?: string;
    orderId?: string;
}

export interface ListItem {
    id: string,
    recycleId?: string,
    size?: number,
    supplierItemId: string,
    supplierId?: string,
    listId: string,
    quantity: number,
    item: CatalogItem,
    targetStock?: number;
    piecePrice?: number;
    isCopy?: boolean,
}

export interface CartList {
    creatorId: string;
    supplierId: string;
    name: string;
    inCart: boolean;
    totalPosition: number;
    positionCount: number;
    itemCount: number;
    id: string;
    totalCartPosition: number;
    totalCartItems: number;
    cartItems: CartListItem[];
    isDeleted: boolean;
}

export interface CartData {
    supplier: CartSupplier,
    lists: CartList[]
    cartItems: CartListItem[],
    summary: ListItem[]
}

interface CartSupplier extends Supplier {
    totalCartPosition: number;
    totalCartItems: number;
    noListCartPosition: number;
    noListCartItems: number;
    showCartTotal: boolean;
    preOrderHint?: string;
    orderQuestion?: string;
}

export interface FilterAttributes {
    category: Record<string, boolean>,
    sortDirection: SORT_METHOD
}

export interface CatalogFilterAttributes extends FilterAttributes {
    lastSupplierId: string
}

export interface LocationSupplier {
    supplierId?: string,
    customerNo?: string,
    postalCode?: string,
    quickstartCode?: string

}

export interface CustomProduct {
    name: string;
    packaging?: string;
    quantity?: number;
    eancode?: string;
    targetStock?: number
}


export interface TimerInterval {
    days: WEEKDAY[],
    every: number,
    time: string

}

export interface SupplierImageInfo {
    img: string | CUSTOM_SUPPLIER_TYPE;
    id: string;
    industry: string;
}

// Classes
class CategoryTag {
    constructor(
        public name: string,
        public id?: string,
        public description?: string
    ) {
    }
}

class Reminder {
    constructor(
        public id?: string,
        public name: string = '',
        public description: string = '',
        public type: REPEAT_CYCLE = REPEAT_CYCLE.ONCE,
        public date: string = DateTime.now().toString(),
        public listId?: string,
        public interval: TimerInterval = {
            days: [],
            every: 1,
            time: ''
        }
    ) {
    }
}

class SupplierCategory {
    public constructor(
        public id = '',
        public name = '',
        public occurrence = 0
    ) {
    }
}

class OrderItem {
    constructor(
        public supplierItemId: string = '',
        public quantity: number = 0,
        public listId: string = ''
    ) {
    }
}

class Order {
    constructor(
        public id: string = '',
        public paymentMethod: PAYMENT_METHOD = PAYMENT_METHOD.UNSPECIFIED,
        public deliveryDate?: string,
        public deliveryTime?: string,
        public pickupDate?: string,
        public pickupTime?: string,
        public pickupLocation?: string,
        public orderCheckbox?: boolean,
        public orderMessage: string | undefined = undefined,
        public promoCode: string | undefined = undefined,
        public items: OrderItem[] = [],
        public supplierId: string = '',
        public supplierName: string = '',
        public positionCount?: number,
        public itemCount?: number,
        public orderNumber?: number,
        public orderedAt?: string,
        public selectedDeliveryType?: string,
        public orderItemsAmount?: number
    ) {
    }
}

export {
    OrderItem,
    Order,
    CategoryTag,
    Reminder,
    SupplierCategory,
}
